import { useDebouncedValue, useLocalStorage } from '@mantine/hooks'
import { useEffect, useState } from 'react'

import { TableColumn, GenericTableProps } from './types'
import { isDateString, parseDateValue } from './dateUtils'
import { filterRows } from './filterUtils'
import { serializeColumns, deserializeColumns, syncColumns } from './columnPersistence'

export interface UseTableOptions<T> {
  /** Source rows. Filtering/sorting/pagination derive from this. */
  data: T[]
  /** Column definitions (single source of truth; visibility/order is persisted, the rest merged fresh). */
  columns: TableColumn<T>[]
  /** localStorage key for persisted column visibility/order. MUST be stable per logical table. */
  tableKey: string
  defaultSortField: keyof T
  defaultSortDirection?: 'asc' | 'desc'
  /** Items per page; Infinity disables pagination. */
  itemsPerPage?: number
  /** Initial filter column. */
  defaultFilterField?: keyof T
  /**
   * Search debounce in ms. 0 = immediate (replaces useFilter); >0 = debounced
   * (replaces useDebouncedFilter). Defaults to 0 so a debounce is never introduced silently.
   */
  searchDebounceMs?: number
}

/**
 * Subset of GenericTable props that this hook fully owns. Spread into
 * `<GenericTable {...table.tableProps} .../>` and add presentational props.
 */
export type TableBundleProps<T> = Pick<
  GenericTableProps<T>,
  | 'data'
  | 'columns'
  | 'onColumnsChange'
  | 'currentPage'
  | 'onPageChange'
  | 'filterField'
  | 'onFilterFieldChange'
  | 'search'
  | 'onSearchChange'
  | 'paginatedData'
  | 'filteredData'
  | 'itemsPerPage'
  | 'isDebouncing'
  | 'sortDirection'
  | 'onSortDirectionChange'
  | 'sortField'
  | 'onSortFieldChange'
>

/**
 * Single hook powering GenericTable: filtering (optionally debounced), sorting
 * (with custom sort functions and date awareness), pagination, and localStorage
 * column persistence. Returns granular state/handlers plus a `tableProps` bundle.
 */
export function useTable<T>({
  data,
  columns,
  tableKey,
  defaultSortField,
  defaultSortDirection = 'desc',
  itemsPerPage = 20,
  defaultFilterField,
  searchDebounceMs = 0,
}: UseTableOptions<T>) {
  // --- filtering ---
  const [filterField, setFilterField] = useState<keyof T | undefined>(defaultFilterField)
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, searchDebounceMs)

  // When not debouncing, filter synchronously off the raw search term.
  const effectiveSearch = searchDebounceMs > 0 ? debouncedSearch : search
  const isDebouncing = searchDebounceMs > 0 ? search !== debouncedSearch : false

  const filteredData = filterRows(data, filterField, effectiveSearch)

  const handleFilterFieldChange = (field: keyof T | undefined) => {
    setFilterField(field)
    setSearch('')
  }
  const handleSearchChange = (value: string) => setSearch(value)

  // --- column persistence ---
  // We persist only a versioned visibility map (see columnPersistence). Columns are always
  // rebuilt from the code definitions, so app updates that add/remove/rename columns can never
  // leave a stale or empty table behind.
  const [tableColumns, setTableColumns] = useLocalStorage<TableColumn<T>[]>({
    key: tableKey,
    defaultValue: columns,
    getInitialValueInEffect: true,
    serialize: cols => serializeColumns(cols),
    deserialize: value => deserializeColumns(value, columns),
  })

  // Re-sync when the code columns change (labels/i18n, dynamic column sets) while keeping the
  // user's current visibility.
  const columnSignature = columns.map(c => `${String(c.key)}:${c.label}`).join('|')
  useEffect(() => {
    setTableColumns(prev => syncColumns(columns, prev))
    // columns/setTableColumns intentionally omitted; columnSignature is the change signal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSignature])

  const visibleColumns = tableColumns.filter(col => col.visible)

  // --- sorting ---
  const [sortField, setSortField] = useState<keyof T>(defaultSortField)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSortDirection)

  const handleSort = (field: keyof T) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedData = [...filteredData].sort((a, b) => {
    const column = tableColumns.find(col => col.key === sortField)
    if (column?.sortFunction) {
      const result = column.sortFunction(a, b)
      return sortDirection === 'asc' ? result : -result
    }

    const aValue = a[sortField]
    const bValue = b[sortField]

    // Null/undefined/N/A always sink to the end.
    if (aValue === null || aValue === undefined || aValue === 'N/A') return 1
    if (bValue === null || bValue === undefined || bValue === 'N/A') return -1

    const isDateColumn = column?.type === 'date' || (isDateString(aValue) && isDateString(bValue))
    if (isDateColumn) {
      const aDate = parseDateValue(aValue)
      const bDate = parseDateValue(bValue)
      if (aDate === null) return 1
      if (bDate === null) return -1
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
    }
    return 0
  })

  // --- pagination ---
  const [currentPage, setCurrentPage] = useState(1)
  // Reset to the first page whenever the filtered result count changes.
  useEffect(() => {
    setCurrentPage(1)
  }, [filteredData.length])

  const handlePageChange = (page: number) => setCurrentPage(page)

  // Clamp so we never show a blank page after the data shrinks.
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage))
  const safePage = Math.min(currentPage, totalPages)

  const paginatedData =
    itemsPerPage === Infinity ? sortedData : sortedData.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage)

  const tableProps: TableBundleProps<T> = {
    data,
    columns: tableColumns,
    onColumnsChange: setTableColumns,
    currentPage: safePage,
    onPageChange: handlePageChange,
    filterField,
    onFilterFieldChange: handleFilterFieldChange,
    search,
    onSearchChange: handleSearchChange,
    paginatedData,
    filteredData,
    itemsPerPage,
    isDebouncing,
    sortDirection,
    onSortDirectionChange: setSortDirection,
    sortField,
    onSortFieldChange: setSortField,
  }

  return {
    // columns
    columns: tableColumns,
    tableColumns,
    setTableColumns,
    visibleColumns,
    // derived data
    filteredData,
    sortedData,
    paginatedData,
    // raw state
    currentPage: safePage,
    sortField,
    sortDirection,
    search,
    filterField,
    isDebouncing,
    // handlers
    handlePageChange,
    handleSort,
    handleSearchChange,
    handleFilterFieldChange,
    setSortField,
    setSortDirection,
    // pre-wired bundle for GenericTable
    tableProps,
  }
}
